import { useEffect, useState } from 'react'

import { loadAllFullGames } from '@runtime/core/user/userContext'
import { useAppStore } from '@runtime/core/store/appStore'
import type {
  GameHistoryEntry,
  ProfileBoardCell,
  ProfileFullGame,
  UserProfile,
} from '@runtime/features/profile/model/profileTypes'

type UseProfileHistoryArgs = {
  open: boolean
  user: UserProfile | null | undefined
}

export function useProfileHistory({ open, user }: UseProfileHistoryArgs) {
  const setThemeId = useAppStore((store) => store.setThemeId)
  const [selectedGame, setSelectedGame] = useState<ProfileFullGame | null>(null)
  const [fullGames, setFullGames] = useState<Record<string, ProfileFullGame>>(
    {},
  )
  const [reviewIndex, setReviewIndex] = useState(0)
  const [reviewPlaying, setReviewPlaying] = useState(false)

  useEffect(() => {
    if (!open) return

    setSelectedGame(null)
    setReviewIndex(0)
    setReviewPlaying(false)

    try {
      const raw = (loadAllFullGames() || {}) as Record<
        string,
        Partial<ProfileFullGame>
      >
      const normalized: Record<string, ProfileFullGame> = {}
      Object.keys(raw).forEach((id) => {
        const full = raw[id]
        if (!full) return
        const board: ProfileBoardCell[][] | undefined = Array.isArray(
          full.board,
        )
          ? full.board
          : Array.isArray(full.finalBoard)
            ? full.finalBoard
            : undefined
        normalized[id] = {
          ...full,
          id,
          board,
        }
      })
      setFullGames(normalized)
    } catch {
      setFullGames({})
      setReviewIndex(0)
      setReviewPlaying(false)
    }
  }, [open])

  useEffect(() => {
    if (!reviewPlaying) return
    if (!selectedGame || !Array.isArray(selectedGame.moves)) return

    const total = selectedGame.moves.length || 0
    if (!total || reviewIndex >= total) {
      setReviewPlaying(false)
      return
    }

    const id = setTimeout(() => {
      setReviewIndex((index) => {
        if (!selectedGame || !Array.isArray(selectedGame.moves)) return 0
        const max = selectedGame.moves.length
        const next = index + 1
        return next > max ? max : next
      })
    }, 700)

    return () => clearTimeout(id)
  }, [reviewPlaying, reviewIndex, selectedGame])

  function applyThemeForGame(game: ProfileFullGame | null) {
    if (!game) return
    const themeId = game.themeId || game.meta?.themeId || null
    if (!themeId) return

    try {
      setThemeId(String(themeId))
    } catch (error) {
      console.warn('[history] applyThemeForGame failed', error)
    }
  }

  function clearSelectedGame() {
    setSelectedGame(null)
    setReviewPlaying(false)
    setReviewIndex(0)
  }

  function handleSelectGame(game: GameHistoryEntry, canReview: boolean) {
    if (!canReview) {
      clearSelectedGame()
      return
    }

    setReviewPlaying(false)
    setReviewIndex(0)

    const full = fullGames[game.id]
    const selected: ProfileFullGame = {
      ...(full || { id: game.id }),
      id: game.id,
      grid: (full && full.grid) || game.grid,
    }

    setSelectedGame(selected)
    applyThemeForGame(selected)
  }

  const favorites = user?.favorites || []
  const selectedGameId = selectedGame ? selectedGame.id : null
  const canReviewSelectedGame = !!(
    selectedGame &&
    Array.isArray(selectedGame.board) &&
    selectedGame.board.length
  )

  return {
    favorites,
    fullGames,
    selectedGame,
    selectedGameId,
    reviewIndex,
    reviewPlaying,
    canReviewSelectedGame,
    setReviewIndex,
    setReviewPlaying,
    handleSelectGame,
  }
}
